import { Watch } from '@Ravnur-Inc/decorators';
import isSame from '@Ravnur-Inc/nanoutils/isSame';
import some from '@Ravnur-Inc/nanoutils/some';
import { setup, Vue } from 'vue-class-component';

import useSecurityStore from '@/store/security';
import { getEmbedForSingleMedia, getEmbedForPlaylist } from '@/transformers/embed';
import {
  Media$Details,
  Media$Type,
  SINGLE_PLAYABLE_MEDIA_TYPES,
  PLAYLIST_MEDIA_TYPES,
} from '@/types/Media';
import TimeCodeInput from '@Ravnur-Inc/core/components/time-code-input/time-code-input';
import EmbedCodeTextarea from '@/modules/live/components/embed-code-textarea';

import './media-embed-code.scss';

enum Player$PlaylistMode {
  BOTTOM = 'bottom',
  RIGHT = 'right',
}

type Embed$Option = {
  id: string;
  label?: string;
  tkey?: string;
  width: string;
  height: string;
};

const CN = 'media-embed-code';
const SINGLE_DEFAULT_KEY = '640x360';
const PLAYLIST_RIGHT_DEFAULT_KEY = '910x360';
const PLAYLIST_BOTTOM_DEFAULT_KEY = '640x516';

const SINGLE_SOURCE_OPTIONS: Embed$Option[] = [
  {
    id: '',
    label: 'Custom',
    tkey: 'share-embed__option-custom',
    width: '',
    height: '',
  },
  {
    id: '100%',
    label: 'Responsive',
    tkey: 'share-embed__option-responsive',
    width: '100%',
    height: '100%',
  },
  { id: '560x315', label: '560x315 (16:9)', width: '560px', height: '315px' },
  { id: '640x360', label: '640x360 (16:9)', width: '640px', height: '360px' },
  { id: '853x480', label: '853x480 (16:9)', width: '853px', height: '480px' },
  { id: '420x315', label: '420x315 (4:3)', width: '420px', height: '315px' },
  { id: '480x360', label: '480x360 (4:3)', width: '480px', height: '360px' },
  { id: '640x480', label: '640x480 (4:3)', width: '640px', height: '480px' },
];

const BOTTOM_PLAYLIST_SOURCES_OPTIONS: Embed$Option[] = [
  {
    id: '',
    label: 'Custom',
    tkey: 'share-embed__option-custom',
    width: '',
    height: '',
  },
  {
    id: '100%',
    label: 'Responsive',
    tkey: 'share-embed__option-responsive',
    width: '100%',
    height: '100%',
  },
  { id: '560x470', label: '560x470 (16:9)', width: '560px', height: '470px' },
  { id: '640x516', label: '640x516 (16:9)', width: '640px', height: '516px' },
  { id: '853x636', label: '853x636 (16:9)', width: '853px', height: '636px' },
  { id: '420x462', label: '420x462 (4:3)', width: '420px', height: '462px' },
  { id: '480x512', label: '480x512 (4:3)', width: '480px', height: '512px' },
  { id: '640x628', label: '640x628 (4:3)', width: '640px', height: '628px' },
];

const RIGHT_PLAYLIST_SOURCES_OPTIONS: Embed$Option[] = [
  {
    id: '',
    label: 'Custom',
    tkey: 'share-embed__option-custom',
    width: '',
    height: '',
  },
  {
    id: '100%',
    label: 'Responsive',
    tkey: 'share-embed__option-responsive',
    width: '100%',
    height: '100%',
  },
  { id: '800x300', label: '800x300 (16:9)', width: '800px', height: '300px' },
  { id: '910x360', label: '910x360 (16:9)', width: '910px', height: '360px' },
  {
    id: '1123x480',
    label: '1123x480 (16:9)',
    width: '1123px',
    height: '480px',
  },
  { id: '592x315', label: '592x315 (4:3)', width: '592px', height: '315px' },
  { id: '608x360', label: '608x360 (4:3)', width: '608px', height: '360px' },
  { id: '910x489', label: '910x489 (4:3)', width: '910px', height: '489px' },
];

class Props {
  origin: Media$Details;
}

export default class MediaEmbedCode extends Vue.with(Props) {
  private security = setup(() => useSecurityStore());

  private size: string = SINGLE_DEFAULT_KEY;
  private from: number | null = null;
  private to: number | null = null;
  private autoStart = false;
  private muted = false;
  private hidePlaylist = false;
  private showAnnotations = false;
  private width = '';
  private height = '';
  private loop = false;

  private playlistMode: Player$PlaylistMode = Player$PlaylistMode.RIGHT;

  get publicDomain() {
    return this.security.publicDomain;
  }

  get type() {
    return this.origin.type;
  }

  get isSinglePlayableSource() {
    return some(isSame(this.type), SINGLE_PLAYABLE_MEDIA_TYPES);
  }

  get isPlaylist() {
    return some(isSame(this.type), PLAYLIST_MEDIA_TYPES);
  }

  get code(): string {
    let { height, width } = this;
    const { publicDomain, autoStart, showAnnotations, from, to, muted, loop } = this;

    if (!this.size) {
      height = height && `${height}px`;
      width = width && `${width}px`;
    }

    if (this.isPlaylist) {
      const params = {
        playlistMode: this.playlistMode,
        playlistId: this.origin.id,
        autoStart: this.autoStart,
        showAnnotations: this.showAnnotations,
        hidePlaylist: this.hidePlaylist,
        muted,
        loop,
      };
      return getEmbedForPlaylist(publicDomain, { height, width }, params);
    }
    const mediaId = this.origin.id;
    const options = { mediaId, autoStart, showAnnotations, from, to, muted, loop };
    return getEmbedForSingleMedia(publicDomain, { height, width }, options);
  }

  get sizes() {
    const { isSinglePlayableSource, playlistMode: mode, hidePlaylist } = this;
    if (isSinglePlayableSource || hidePlaylist) {
      return SINGLE_SOURCE_OPTIONS;
    }

    return mode === Player$PlaylistMode.BOTTOM
      ? BOTTOM_PLAYLIST_SOURCES_OPTIONS
      : RIGHT_PLAYLIST_SOURCES_OPTIONS;
  }

  get minFrom() {
    return 0;
  }

  get maxFrom() {
    const {
      to,
      origin: { duration },
    } = this;
    if (to) {
      return Math.min(duration || Number.MAX_VALUE, +to);
    }
    return duration || Number.MAX_VALUE;
  }

  get minTo() {
    return this.from || 0;
  }

  get maxTo() {
    const {
      origin: { duration },
    } = this;
    return duration || Number.MAX_VALUE;
  }

  get isAnnoAvailable() {
    return this.type === Media$Type.VIDEO;
  }

  get canCopy() {
    const { height, size, width } = this;
    const w = parseInt(width || '0', 10);
    const h = parseInt(height || '0', 10);
    return !(!size && (!w || !h));
  }

  get timeFormat() {
    return (this.origin.duration || 0) >= 3600 ? 'hh:mm:ss' : 'mm:ss';
  }

  @Watch('size', { immediate: true })
  protected handleChangeSize() {
    const [size] = this.sizes.filter((s) => s.id === this.size);
    if (size) {
      this.width = size.width;
      this.height = size.height;
    }
  }

  @Watch('playlistMode', { immediate: true })
  protected handlePlaylistModeChanged(mode: Player$PlaylistMode) {
    if (this.isSinglePlayableSource) {
      return;
    }
    this.size =
      mode === Player$PlaylistMode.BOTTOM
        ? PLAYLIST_BOTTOM_DEFAULT_KEY
        : PLAYLIST_RIGHT_DEFAULT_KEY;
  }

  @Watch('hidePlaylist', { immediate: true })
  protected handleHidePlaylistChanged(isHidden: boolean) {
    if (isHidden) {
      this.size = SINGLE_DEFAULT_KEY;
    } else {
      this.handlePlaylistModeChanged(this.playlistMode);
    }
  }

  render() {
    return (
      <div class={CN}>
        <div class={`${CN}__column`}>
          <div class={`${CN}__row ${CN}__location`} v-show={this.isPlaylist}>
            <l10n class={`${CN}__label`} group="media" tkey="embed__playlist-mode" />
            <radiobutton
              class={`${CN}__field`}
              disabled={this.hidePlaylist}
              label={this.$t('media', 'embed__right-playlist')}
              vModel={[this.playlistMode, 'selected']}
              value={Player$PlaylistMode.RIGHT}
            />

            <radiobutton
              class={`${CN}__field`}
              disabled={this.hidePlaylist}
              label={this.$t('media', 'embed__bottom-playlist')}
              vModel={[this.playlistMode, 'selected']}
              value={Player$PlaylistMode.BOTTOM}
            />
          </div>

          <chosen
            class={`${CN}__field`}
            label={this.$t('media', 'embed__size')}
            options={this.sizes}
            vModel={[this.size, 'value']}
          />

          <div class={`${CN}__row`} v-show={!this.size}>
            <text-field
              class={`${CN}__field`}
              formatter={/^\d+$/}
              label={this.$t('media', 'embed__width')}
              vModel={[this.width, 'value']}
            />

            <text-field
              class={`${CN}__field`}
              formatter={/^\d+$/}
              label={this.$t('media', 'embed__height')}
              vModel={[this.height, 'value']}
            />
          </div>

          <div class={`${CN}__row ${CN}__row--timecodes`} v-show={this.isSinglePlayableSource}>
            <TimeCodeInput
              class={`${CN}__field`}
              format={this.timeFormat}
              label={this.$t('media', 'embed__from')}
              max={this.maxFrom}
              min={this.minFrom}
              placeholder="00:00"
              showSwitcher={false}
              step={1}
              stepUnit="second"
              vModel={[this.from, 'value']}
            />

            {/* <TimeCodeInput
              class={`${CN}__field`}
              format={this.timeFormat}
              label={this.$t('media', 'embed__to')}
              max={this.maxTo}
              min={this.minTo}
              placeholder="00:00"
              showSwitcher={false}
              step={1}
              stepUnit="second"
              vModel={[this.to, 'value']}
            /> */}
          </div>

          <l10n className={`${CN}__label`} group="media" tkey="embed__options" />

          <div>
            <checkbox
              label={this.$t('common', 'embed-code__autostart')}
              vModel={[this.autoStart, 'value']}
            />
          </div>

          <div>
            <checkbox
              label={this.$t('common', 'embed-code__mute')}
              vModel={[this.muted, 'value']}
            />
          </div>

          <div>
            <checkbox label={this.$t('common', 'embed-code__loop')} vModel={[this.loop, 'value']} />
          </div>

          {/* <div v-show={this.isPlaylist}>
            <checkbox
              label={this.$t('media', 'share-embed__hide-playlist')}
              vModel={[this.hidePlaylist, 'value']}
            />
          </div> */}
        </div>

        <div class={`${CN}__column`}>
          <EmbedCodeTextarea canCopy={this.canCopy} class={`${CN}__embed`} code={this.code} />
        </div>
      </div>
    );
  }
}
